<!-- @format -->
<script setup>
    import SmartBox from '../common/SmartBox.vue';
    import DestinationSearch from './components/DestinationSearch.vue';
    import Occupancy from './components/Occupancy.vue';
    import SearchInput from './components/SearchInput.vue';
    import BookerCalendar from './components/BookerCalendar.vue';
    import { CalendarAvailability } from "../../utils/pricetravel/shared/calendarAvailability";
    import { storeToRefs } from 'pinia'
    import { usePackageBookerStore } from '../stores/packageBooker'
    import { ref, getCurrentInstance, onMounted, computed, watch, nextTick } from 'vue'

    const { proxy } = getCurrentInstance()
    const __ = proxy.__
    const fn = window.__pt.fn;
    const config = window.__pt.settings.site;
    const defaultDate = fn.getDate();
    const guestsBox = ref(null)
    const calendarBox = ref(null)
    const calendarPrices = ref({});
    const isMobile = ref(false);
    const props = defineProps({
        isList: {
            type: String,
            default: 'false'
        },
        isDetail: {
            type: [String, Boolean],
            default: false
        },
        isSidebar: {
            type: [String, Boolean],
            default: false
        }
    })

    const emits = defineEmits(['searchDetail'])
    const store = usePackageBookerStore()
    const {
        form,
        fieldErrors,
        book,
        recentSearches,
        popularDestinations,
        searchResults,
        algoliaUrl,
        OpenSearch,
        CloseSearch,
        lastQuery,
        settingPaxes,
        localeDatePicker
    } = storeToRefs(store)
    const isModalOpen = ref(false);
    const summaryBtn = ref(null);
    const modalCloseBtn = ref(null);

    function closeModal() {
        isModalOpen.value = false;
    }

    // Focus management for the mobile "modify search" dialog (WCAG 2.4.3)
    watch(isModalOpen, (open) => {
        nextTick(() => {
            if (open) {
                modalCloseBtn.value?.focus();
            } else {
                summaryBtn.value?.focus();
            }
        });
    });

    const isDetailMode = computed(() => {
        return props.isDetail === 'true' || props.isDetail === true;
    });
    const calendarDates = computed({
        get() {
            return {
                start: book.value.checkIn ? new Date(book.value.checkIn) : null,
                end: book.value.checkOut ? new Date(book.value.checkOut) : null
            };
        },
        set(val) {
            book.value.checkIn = val.start ? new Date(val.start) : null;
            book.value.checkOut = val.end ? new Date(val.end) : null;
            updateFormDates(book.value.checkIn, book.value.checkOut);
        }
    });

    const shortDates = computed(() => {
        if (!book.value.checkIn) return '';
        const format = localeDatePicker.value.formatMobile;
        const start = dayjs(book.value.checkIn).format(format);
        const end = book.value.checkOut ? dayjs(book.value.checkOut).format(format) : '';
        return end ? `${start} - ${end}` : start;
    });

    const shortGuests = computed(() => {
        const people = store.totalAdults + store.totalChildren;
        const rooms = Array.isArray(book.value.paxes) ? book.value.paxes.length : (book.value.rooms || 1);
        const strPers = __('booker.pill_pers');
        const strHab = __('booker.pill_hab');
        return `${people} ${strPers} ${rooms} ${strHab}`;
    });

    function onSelectDates(val) {
        updateFormDates(val.start, val.end);
        book.value.checkIn = val.start;
        book.value.checkOut = val.end;
        if (calendarBox.value) {
            calendarBox.value.close();
        }

        if (val.start && val.end && isDetailMode.value) {
            isValidFormApply();
        }
    }

    let origin = null;
    function isValidDate(dateFrom, dateTo) {
        let valid = true;
        dateFrom.setHours(0, 0, 0, 0);
        if (+dateFrom > +dateTo) {
            valid = false;
        }
        return valid;
    }

    function initDatePicker() {
        let valid = isValidDate(book.value.checkIn, book.value.checkOut);
        if (!valid) {
            book.value.checkOut = store.getInitialDate(book.value.checkIn, config.bookerDefaultCheckOutDay);
        }
        updateFormDates(book.value.checkIn, book.value.checkOut);
    }

    function updateFormDates(start, end) {
        if (!start || !end) return;
        const format = localeDatePicker.value.format;
        form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')} - ${dayjs(end).format(format).replace(/\./g, '')}`;
    }

    async function fetchAvailability(viewDate) {
        const hotelId = window.__pt.data?.hotelId;
        if (!hotelId) return;

        const targetM = dayjs(viewDate);
        const year = targetM.format('YYYY');
        const month = targetM.format('MM');

        const response = await CalendarAvailability.getAvailability(
            hotelId,
            year,
            month,
            false,
            true
        );
        if (response && response.daysByDate) {
            calendarPrices.value = { ...calendarPrices.value, ...extractPrices(response.daysByDate) };
        }
    }

    function extractPrices(daysByDate) {
        const prices = {};
        for (const key in daysByDate) {
            const day = daysByDate[key];
            prices[day.checkinFormat] = {
                available: day.available,
                avgPrice: day.available ? (() => {
                    const price = roundNumber(day.avgPrice);
                    return price != 0 ? `$${price}` : '';
                })() : null,
                color: day.color
            };
        }
        return prices;
    }

    function roundNumber(val) {
        const currency = __pt.settings.site.currency;
        if (currency === 'COP') {
            const suffix = val.slice(-1);
            let number = parseFloat(val.slice(0, -1));
            return `${customRound(number)}${suffix}`;
        } else if (currency !== 'COP' && (val.endsWith('K') || val.endsWith('M'))) {
            return val;
        } else {
            return customRound(val);
        }
    }

    function customRound(num) {
        const decimal = num % 1;
        const integer = Math.floor(num);

        if (decimal > 0.5) {
            return integer + 1;
        } else {
            return num;
        }
    }

    function onOpenSearch(element) {
        setTimeout(() => {
            store.fetchPopularDestinations(element, origin);
        }, 200);
    }

    async function onSearch(value, mobile, list) {
        if (mobile && list) {
            form.value[list] = value;
        }
        await store.fetchSearchResults(value, mobile, list);
    }

    function onSelectDestination(item, type, suffix = '') {
        const code = store.selectDestination(item, type, suffix);
        store.setFieldError(type, "");
        if (type === 'from') {
            origin = code;
        }
    }

    function onChangePaxes(payload) {
        book.value.paxes = payload || book.value.paxes;
        store.updateInputPaxes();
    }

    function onApplyPaxes(payload) {
        book.value.paxes = payload || book.value.paxes;
        store.updateInputPaxes();
        isValidFormApply();
        if (guestsBox.value && typeof guestsBox.value.close === 'function') {
            guestsBox.value.close();
        }
    }
    function onCancelPaxes() {
        store.updateInputPaxes();
        if (guestsBox.value && typeof guestsBox.value.close === 'function') {
            guestsBox.value.close();
        }
    }
    function onCloseGuests() {
        store.updateInputPaxes();
    }
    function isValidFormApply() {
        if (isDetailMode.value) {
            if (form.value.dates) {
                submitSearch();
            }
            return;
        }
        if (store.validateForm(true)) {
            submitSearch();
        }
    }
    function submitSearch() {
        if (isDetailMode.value) {
            if (!form.value.dates) {
                store.setFieldError('dates', __('booker.select_dates'));
                return;
            }
            const detailData = store.paramsData();
            const url = getUrlWithQueryString(detailData)
            window.location.href = url;
            return;
        }
        store.submitSearch();
    }

    const hasDataFrom = computed(() => {
        return (searchResults.value?.from?.length || 0) > 0 ||
            (recentSearches.value?.from?.length || 0) > 0 ||
            (popularDestinations.value?.from?.length || 0) > 0;
    })

    const hasDataTo = computed(() => {
        return (searchResults.value?.to?.length || 0) > 0 ||
            (recentSearches.value?.to?.length || 0) > 0 ||
            (popularDestinations.value?.to?.length || 0) > 0;
    })

    const shortPax = computed(() => {
        return store.paxSummary;
    });

    const getUrlWithQueryString = (query, baseUrl) => {
        const queryParams = new URLSearchParams(query);
        const url = baseUrl ? baseUrl : `${window.location.origin}${window.location.pathname}`;
        return `${url}?${queryParams.toString()}`;
    };


    onMounted(() => {
        store.initialDefaultDate();
        store.initSearchBox();
        initDatePicker();
        store.loadSearchHistory();
    })
</script>
<template>
    <div class="booker-wrapper"
         :class="{ 'is-list-view': isList === 'true' || isList === true, 'is-modal-open': isModalOpen, 'is-detail-view': isDetailMode, 'is-sidebar-view': isSidebar === 'true' || isSidebar === true }">
        <!-- Summary Card (Mobile Only) -->
        <button type="button"
                class="summary-card"
                ref="summaryBtn"
                @click="isModalOpen = true"
                :aria-label="__('booker.modify_search')">
            <span class="summary-card__box">
                <span class="summary-card__data">

                    <span class="summary-card__dates">
                        <span class="icon font-icons icons-calendar-depart font-20"></span>
                        <span>{{ form.dates }}</span>
                    </span>

                    <span class="summary-card__separator">|</span>

                    <span class="summary-card__paxes">
                        <span class="icon font-icons icons-guests font-20"></span>
                        <span>{{ shortGuests }}</span>
                    </span>

                </span>
            </span>
        </button>

        <!-- Form Container -->
        <div class="booker-form-container">
            <div class="booker-modal__overlay" @click="isModalOpen = false" aria-hidden="true"></div>
            <div class="booker-modal__content" :role="isModalOpen ? 'dialog' : null"
                 :aria-modal="isModalOpen ? 'true' : null" aria-labelledby="booker-modal-title"
                 @keydown.esc="closeModal">

                <header class="booker-modal__header">
                    <h2 id="booker-modal-title" class="font-16 font-500 font-main m-0">{{ __('booker.modify_search') }}</h2>
                    <button type="button" class="booker-modal__close" ref="modalCloseBtn" @click="isModalOpen = false"
                            :aria-label="__('messages.close')">
                        <span class="icon font-icons icons-close font-24" aria-hidden="true"></span>
                    </button>
                </header>
                <form class="search" @submit.prevent="submitSearch" role="search" :aria-label="__('booker.flights')">
                    <header v-if="isSidebar === 'true' || isSidebar === true" class="sidebar-header d-none d-md-block">
                        <h2 class="font-20 font-500 font-main m-0">{{ __('booker.modify_search') }}</h2>
                    </header>
                    <div class="search__container" :class="{ 'is-side-bar': isSidebar === 'true' || isSidebar === true, 'w-100': isDetailMode }">
                        <div class="search__fligths" :class="{ 'is-side-bar': isSidebar === 'true' || isSidebar === true, 'is-detail-mobile-pill-container': isDetailMode }">
                            <div class="search__item" >
                                <SmartBox @open="onOpenSearch('from')" :title="__('booker.origin_sub')" :OpenBox="OpenSearch.from" :class="{'h-100 detail-mobile-smartbox justify-content-start px-2': isDetailMode}"
                                          :CloseBox="CloseSearch.from" :isHeaderActive="false" :hasContent="hasDataFrom" @isMobile="isMobile = $event">
                                    <template #trigger>
                                        <SearchInput id="origin" class="d-none-detail-mobile" :label="__('booker.origin')" :placeholder="__('booker.origin')"
                                                     :activeFocus="!isMobile" v-model="form.from" @input="onSearch($event, false, 'from')" :debounce="700"
                                                     :selectOnFocus="!isMobile" :autocomplete="'off'" :spellcheck="false" :error="!!fieldErrors.from"
                                                     :errorMessage="fieldErrors.from" :readonly="isMobile">
                                            <template #icon>
                                                <span class="search__icon font-20 icons-plane-departure" aria-hidden="true"></span>
                                            </template>
                                        </SearchInput>
                                        <div class="pill-half d-flex-detail-mobile align-items-center justify-content-center flex-grow-1" v-if="isDetailMode">
                                            <span class="icon font-icons icons-plane-departure font-20"></span>
                                            <span class="pill-text">{{ form.from }}</span>
                                        </div>
                                    </template>
                                    <template #content>
                                        <DestinationSearch :product="'P'" :recentSearches="recentSearches.from" :origin="'from'"
                                                           :popularDestinations="popularDestinations.from" @update:query="onSearch($event, true, 'from')" :typeBooker="'from'"
                                                           :destination="form.from" :imgUrl="algoliaUrl" :searchResults="searchResults.from"
                                                           @update:selectedDestination="onSelectDestination($event, 'from', '')" :lastQuery="lastQuery"
                                                           :label="__('booker.origin')" :placeholder="''" :icon="'icons-plane-departure'">
                                        </DestinationSearch>
                                    </template>
                                </SmartBox>
                            </div>
                            <div class="search__item" v-if="!isDetailMode">
                                <SmartBox @open="onOpenSearch('to')" :title="__('booker.destination_sub')" :OpenBox="OpenSearch.to"  :class="{'h-100 detail-mobile-smartbox': isDetailMode}"
                                          :CloseBox="CloseSearch.to" :isHeaderActive="false" :hasContent="hasDataTo" @isMobile="isMobile = $event">
                                    <template #trigger>
                                        <SearchInput id="destination"  :label="__('booker.destination')" :placeholder="__('booker.destination')"
                                                     :selectOnFocus="!isMobile" :activeFocus="!isMobile" v-model="form.to" @input="onSearch($event, false, 'to')"
                                                     :debounce="700" :autocomplete="'off'" :spellcheck="false" :error="!!fieldErrors.to"
                                                     :errorMessage="fieldErrors.to" :readonly="isMobile">
                                            <template #icon>
                                                <span class="search__icon font-20 icons-plane-arrival" aria-hidden="true"></span>
                                            </template>
                                        </SearchInput>
                                    </template>
                                    <template #content>
                                        <DestinationSearch :product="'P'" :recentSearches="recentSearches.to"
                                                           :popularDestinations="popularDestinations.to" @update:query="onSearch($event, true, 'to')" :typeBooker="'to'"
                                                           :destination="form.to" :imgUrl="algoliaUrl" :searchResults="searchResults.to"
                                                           @update:selectedDestination="onSelectDestination($event, 'to', '_to')" :lastQuery="lastQuery"
                                                           :label="__('booker.destination')" :placeholder="''" :icon="'icons-plane-arrival'">
                                        </DestinationSearch>
                                    </template>
                                </SmartBox>
                            </div>
                        </div>
                        <div class="search__guest" :class="{ 'is-side-bar': isSidebar === 'true' || isSidebar === true, 'is-detail-mobile-pill-container': isDetailMode }">
                            <div class="search__item">
                                <SmartBox ref="calendarBox" :title="__('booker.dates')" :isHeaderActive="false" :mobile-breakpoint="768" :class="{'h-100 detail-mobile-smartbox': isDetailMode}">
                                    <template #trigger>
                                        <SearchInput id="calendar-checkIn-package" class="d-none-detail-mobile" type="text" required name="calendar-checkIn-package"
                                                     :activeFocus="true" :label="__('booker.dates')" :readonly="true" v-model="form.dates"
                                                     :error="!!fieldErrors.dates" :errorMessage="fieldErrors.dates">
                                            <template #icon>
                                                <span class="icon font-icons icons-calendar-depart font-20" aria-hidden="true"></span>
                                            </template>
                                        </SearchInput>
                                        <div class="pill-half d-flex-detail-mobile align-items-center justify-content-center flex-grow-1" v-if="isDetailMode">
                                            <span class="icon font-icons icons-calendar-depart font-20"></span>
                                            <span class="pill-text">{{ shortDates }}</span>
                                        </div>
                                    </template>
                                    <template #content>
                                        <BookerCalendar :modelValue="calendarDates" :currency="config.currency" :isPackage="true" mode="range" :unit="'nights'"
                                                        :minDays="2" :nOffset="-1" :minDate="defaultDate" :prices="calendarPrices"
                                                        @viewDate="fetchAvailability" @select="onSelectDates" @close="calendarBox.close()" />
                                    </template>
                                </SmartBox>
                            </div>
                            <div class="pill-divider d-flex-detail-mobile" v-if="isDetailMode"></div>
                            <div class="search__item">
                                <SmartBox ref="guestsBox" :title="__('booker.guests_sub')" :isHeaderActive="false" @close="onCloseGuests"
                                          :mobile-breakpoint="768" :class="{'h-100 detail-mobile-smartbox': isDetailMode}">
                                    <template #trigger>
                                        <SearchInput id="guests" class="d-none-detail-mobile" :label="__('booker.guests')" :placeholder="form.guests" :readonly="true"
                                                     :activeFocus="true" v-model="form.guests" :error="!!fieldErrors.guests"
                                                     :errorMessage="fieldErrors.guests">
                                            <template #icon>
                                                <span class="icon font-icons icons-guests font-20" aria-hidden="true"></span>
                                            </template>
                                        </SearchInput>
                                        <div class="pill-half d-flex-detail-mobile align-items-center justify-content-center flex-grow-1" v-if="isDetailMode">
                                            <span class="icon font-icons icons-guests font-20"></span>
                                            <span class="pill-text">{{ shortGuests }}</span>
                                        </div>
                                    </template>
                                    <template #content>
                                        <Occupancy :product="'P'" :settingPaxes="settingPaxes" :counts="book.paxes" @change="onChangePaxes"
                                                   @apply="onApplyPaxes" @cancel="onCancelPaxes">
                                        </Occupancy>
                                    </template>
                                </SmartBox>
                            </div>
                            <div  class="search__action d-none-detail-mobile" :class="{ 'is-side-bar': isSidebar === 'true' || isSidebar === true }">
                                <button type="submit" class="search__button " :class="{'search__btn btnSecondary btnSecondary--md': isDetailMode, 'btnPrimary btnPrimary--md': !isDetailMode}">
                                    {{ __('booker.button') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .search {
        background-color: var(--bg-info-strong);
        padding: var(--space-16);
        border-radius: var(--space-12);
        box-sizing: border-box;
        background-color: var(--bg-base);
        border: 1px solid var(--border-subtle);
        margin: var(--space-16);
        box-shadow: var(--shadow-200);

        @media (min-width: 576px) {
            width: 544px;
            margin: var(--space-16) auto;
        }

        @media (min-width: 768px) {
            width: auto;
            padding: var(--space-20) 0px;
            border-radius: 0px 0px var(--space-12) var(--space-12);
            box-shadow: none;
            margin: 0px auto;
            border: 0px;
        }

        @media (min-width: 1280px) {
            width: 100%;
            padding: var(--space-20);
            border-radius: var(--space-12);
            border: 1px solid var(--border-subtle);
        }

        &__container {
            display: flex;
            flex-direction: column;
            gap: var(--space-16);

            @media (min-width: 768px) {
                gap: var(--space-20);
            }

            @media (min-width: 1280px) {
                flex-direction: row;
                align-items: center;
                gap: var(--space-12);
            }

            &.is-side-bar {
                flex-direction: column;
            }
        }

        &__fligths,
        &__guest {
            display: flex;
            flex-direction: column;
            gap: var(--space-16);

            @media (min-width: 768px) {
                flex-direction: row;
                align-items: center;
                gap: var(--space-32);
            }

            @media (min-width: 1280px) {
                flex: 1;
                gap: var(--space-12);
            }

            &.is-side-bar {
                width: 100%;
                flex-direction: column;
            }
        }

        &__fligths {
            @media (min-width: 1280px) {
                flex: 1;
                max-width: 552px;
            }

            &.is-detail-mobile-pill-container {
              max-width: 440px;
            }
        }

        &__item {
            flex: 1;
            width: 100%;
            min-width: 0;
        }

        &__action {
            width: 100%;

            @media (min-width: 768px) {
                width: 160px;
            }

            @media (min-width: 1280px) {
                width: 114px;
            }

            &.is-side-bar {
                width: 100%;
            }
        }

        &__button {
            width: 100%;
        }
    }

    .option {
        display: flex;
        align-items: center;
        gap: var(--space-8);
        cursor: pointer;
        margin: 0px;
    }

    .booker-wrapper {
        width: 100%;
        max-width: 1252px;
    }

        .booker-wrapper.is-list-view {
            @media (max-width: 767px) {
                margin-left: calc(-50vw + 50%);
                margin-right: calc(-50vw + 50%);
                width: 100vw;
                background: var(--bg-base);
                border-bottom: none;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                z-index: 10;

                .summary-card {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: var(--space-16);
                    cursor: pointer;
                    width: 100%;
                    max-width: 576px;
                    background: none;
                    border: none;
                    padding: var(--space-8) var(--space-16);
                    margin: 0 auto;
                    text-align: left;
                    font: inherit;
                    color: inherit;

                    &__box {
                        flex: 1;
                        display: flex;
                        align-items: center;
                        gap: var(--space-8);
                        padding: var(--space-8) var(--space-16);
                        border: 1px solid var(--border-subtle);
                        border-radius: 999px;
                        background: var(--bg-base);
                        overflow: hidden;
                    }

                    &__icon {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;

                        .icon {
                            display: flex;
                        }
                    }

                    &__data {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        flex: 1;
                        min-width: 0;
                    }
                    &__dates,
                    &__paxes {
                        display: flex;
                        align-items: center;
                        gap: var(--space-4);
                        min-width: 0;
                        flex: 1 1 50%;
                        width: 50%;
                        > span:last-child {
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            min-width: 0;
                        }

                        .icon {
                            flex-shrink: 0;
                        }
                    }
                    &__separator {
                        padding: 0 var(--space-12);
                        flex-shrink: 0;
                        color: var(--text-muted);
                    }

                    &__dest {
                        color: var(--text-main);
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    &__info {
                        color: var(--text-muted);
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    &__btn {
                        width: 48px;
                        height: 48px;
                        border-radius: 50%;
                        background-color: var(--bg-primary);
                        color: var(--text-oncolor);
                        border: none;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        flex-shrink: 0;
                    }
                }

                &.is-modal-open .summary-card {
                    display: none;
                }

                .booker-form-container {
                    display: grid;
                    grid-template-rows: 0fr;
                    transition: grid-template-rows 0.35s cubic-bezier(0.22, 1, 0.36, 1);
                    max-width: 544px;
                    margin: 0 auto;

                    .booker-modal__overlay {
                        display: none;
                    }

                    .booker-modal__content {
                        overflow: hidden;
                        min-height: 0;
                        background-color: var(--bg-base);
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        opacity: 0;
                        transition: opacity 0.12s ease;
                    }

                    .booker-modal__header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: var(--space-16) var(--space-16) 0px var(--space-16);

                        h2 {
                            font-family: var(--basefontFamily);
                        }
                    }

                    .booker-modal__close {
                        background: transparent;
                        border: none;
                        cursor: pointer;
                        display: flex;
                        color: var(--text-main);

                        &:focus-visible {
                            outline: 2px solid var(--border-focus, var(--bg-primary));
                            outline-offset: 2px;
                            border-radius: var(--space-4, 4px);
                        }
                    }

                    .search {
                        display: flex;
                        flex-direction: column;
                        box-shadow: none;
                        border: none;
                        margin: 0;
                        width: 100%;

                        .search__inputs {
                            display: flex;
                            flex-direction: column;
                            gap: var(--space-16, 16px);
                        }

                        .search__action {
                            display: flex;
                            justify-content: stretch;

                            .search__btn {
                                width: 100%;
                            }
                        }
                    }
                }

                &.is-modal-open .booker-form-container {
                    grid-template-rows: 1fr;

                    .booker-modal__content {
                        opacity: 1;
                        transition: opacity 0.2s ease 0.14s;
                    }
                }

                @media (prefers-reduced-motion: reduce) {

                    .booker-form-container,
                    .booker-form-container .booker-modal__content {
                        transition: none;
                    }
                }
            }

            @media (min-width: 768px) {
                margin: 0;
                width: auto;
                position: static;
                transform: none;

                .summary-card {
                    display: none;
                }

                .booker-form-container {
                    display: block;
                }

                .booker-modal__overlay,
                .booker-modal__header {
                    display: none;
                }
            }
        }

        .booker-wrapper:not(.is-list-view) {

            .summary-card,
            .booker-modal__overlay,
            .booker-modal__header {
                display: none;
            }
        }

        .booker-wrapper.is-detail-view {
            .booker-form-container {
                display: block;
            }

            .search {
                margin: 0;
                box-shadow: none;

                @media (max-width: 767px) {
                    border: none;
                    background-color: transparent;
                    padding: 0;
                }

                @media (min-width: 768px) {
                    display: flex;
                    flex-direction: row;
                    align-items: stretch;
                    justify-content: space-between;
                    gap: var(--space-16);
                    width: 100%;
                }

                .search__inputs {
                    @media (min-width: 768px) {
                        flex: 1;
                        display: flex;
                        flex-direction: row;
                        gap: var(--space-16);
                        width: 100%;

                        > * {
                            flex: 1;
                        }
                    }
                }

                .search__action {
                    @media (min-width: 768px) {
                        flex: 0 0 auto;
                        margin: 0;
                        display: flex;
                        align-items: stretch;
                        width: auto;
                        .search__btn {
                            width: auto;
                            padding: 0 var(--space-32);
                            min-width: 200px;
                            height: auto;
                            min-height: 56px;
                        }
                    }
                }
            }

            .d-flex-detail-mobile {
                display: none;
            }

            @media (max-width: 767px) {
                .d-none-detail-mobile {
                    display: none;
                }

                .d-flex-detail-mobile {
                    display: flex;
                }

                .is-detail-mobile-pill-container {
                    flex-direction: row;
                    align-items: stretch;
                    border: 1px solid var(--border-subtle);
                    background-color: var(--bg-base);
                    border-radius: var(--space-8);
                    padding: 0;
                    gap: 0;
                    margin: 0 auto;
                    width: 100%;
                    height: 48px;
                }

                .detail-mobile-smartbox {
                    flex: 1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .pill-half {
                    flex: 1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: var(--space-8);
                    padding: 0 var(--space-8);
                    font-size: 16px;
                    color: var(--text-main);
                    cursor: pointer;
                    height: 100%;
                    width: 100%;
                    background: transparent;
                    min-width: 0;
                }

                .pill-text {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .pill-divider {
                    width: 1px;
                    height: auto;
                    background-color: var(--border-subtle);
                    margin: var(--space-8) 0;
                }
            }
        }

        .booker-wrapper.is-sidebar-view {
            .search {
                @media (min-width: 768px) {
                    display: flex;
                    flex-direction: column;
                    gap: 0;
                    border-radius: var(--space-12);

                    .sidebar-header {
                        padding-bottom: var(--space-16);
                        width: 100%;
                    }

                    .search__item {
                        display: flex;
                        flex-direction: column;
                        gap: var(--space-16);
                    }

                    .search__action {
                        margin-top: var(--space-16);

                        .search__btn {
                            width: 100%;
                        }
                    }
                }
            }

            :deep(.field) {
                gap: var(--space-4);
                padding: 0px var(--space-8);
            }

            :deep(.field__label) {
                font-size: 0.688rem !important;
            }
        }
</style>
